import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import {
	SavedSearchActivityRowResource,
	SavedSearchActivityReportResource,
	SavedSearchActivityQuery,
} from "api/resource/saved-search-activity";
import { loginReset } from "./authentication";

/**
 * Saved Search Activity report (statsSavedSearches.cfm) — MAX-3885. Read-only, server-paged: one page
 * of rows + total count. Mirrors the reports-leads report slice.
 */
export interface SavedSearchActivityState {
	loading?: boolean;
	error?: string | null;
	loaded: boolean;
	rows: SavedSearchActivityRowResource[];
	totalCount: number;
	filter: SavedSearchActivityQuery | null;
}

const initialState: SavedSearchActivityState = {
	loaded: false,
	rows: [],
	totalCount: 0,
	filter: null,
};

const fetchSavedSearchActivity = createAsyncThunk(
	"savedSearchActivity/fetch",
	async (payload: SavedSearchActivityQuery, thunkAPI) => {
		thunkAPI.dispatch(savedSearchActivityLoading());
		const result = api.getSavedSearchActivity(payload);
		result
			.then((report) => thunkAPI.dispatch(savedSearchActivityLoaded({ report, filter: payload })))
			.catch((error) => thunkAPI.dispatch(savedSearchActivityFailed(error)));
		return result;
	}
);

export const savedSearchActivitySlice = createSlice({
	name: "savedSearchActivity",
	initialState,
	reducers: {
		savedSearchActivityLoading(state) {
			state.loading = true;
		},
		savedSearchActivityLoaded(
			state,
			action: PayloadAction<{ report: SavedSearchActivityReportResource; filter: SavedSearchActivityQuery }>
		) {
			state.loading = false;
			state.error = null;
			state.loaded = true;
			state.rows = action.payload.report.rows;
			state.totalCount = action.payload.report.totalCount;
			state.filter = action.payload.filter;
		},
		savedSearchActivityFailed(state, action: PayloadAction<any>) {
			state.loading = false;
			state.error = action.payload && action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	savedSearchActivityLoading,
	savedSearchActivityLoaded,
	savedSearchActivityFailed,
} = savedSearchActivitySlice.actions;

export { fetchSavedSearchActivity };
